import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Users, GraduationCap, ClipboardCheck, Wallet, UserPlus, Plus } from "lucide-react";

function Stat({ label, value, icon: Icon, color = "bg-primary/10 text-primary" }) {
  return (
    <Card className="rounded-2xl border-border/70 shadow-sm">
      <CardContent className="flex items-center gap-4 p-6">
        <div className={`grid h-12 w-12 place-items-center rounded-xl ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-sm text-muted-foreground">{label}</div>
          <div className="num text-3xl font-semibold tracking-tight">
            {value ?? "—"}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function AdminHome() {
  const { t } = useI18n();
  const [summary, setSummary] = useState({});
  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
          {t("dashboard")}
        </h1>
        <p className="mt-1 text-muted-foreground">{t("admin_dashboard_title")}</p>
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Stat label={t("students")} value={summary.students} icon={GraduationCap} />
        <Stat label={t("parent") + "s"} value={summary.parents} icon={Users} color="bg-secondary/10 text-secondary" />
        <Stat label={t("teacher") + "s"} value={summary.teachers} icon={Users} color="bg-accent/20 text-accent-foreground" />
        <Stat label={t("staffRole")} value={summary.staff} icon={Users} />
        <Stat label={t("attendance") + " " + t("students")} value={summary.students_today} icon={ClipboardCheck} color="bg-primary/10 text-primary" />
        <Stat label={t("staff") + " " + t("attendance")} value={summary.staff_checkins_today} icon={ClipboardCheck} color="bg-secondary/10 text-secondary" />
        <Stat label={t("payments")} value={summary.payments_today} icon={Wallet} color="bg-accent/20 text-accent-foreground" />
      </div>
    </div>
  );
}

export function UsersPage() {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "teacher" });

  const load = () => api.get("/users").then((r) => setUsers(r.data.users));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", password: "", role: "teacher" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("users")}</h1>
          <p className="text-sm text-muted-foreground">Manage staff, teachers and parents.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-user-btn" className="rounded-full gap-2">
              <UserPlus className="h-4 w-4" />
              {t("createUser")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("createUser")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("fullName")}</Label><Input data-testid="cu-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" /></div>
              <div><Label>{t("email")}</Label><Input data-testid="cu-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11" /></div>
              <div><Label>{t("phone")}</Label><Input data-testid="cu-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11" /></div>
              <div><Label>{t("password")}</Label><Input data-testid="cu-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-11" /></div>
              <div>
                <Label>{t("role")}</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="cu-role" className="h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">{t("staffRole")}</SelectItem>
                    <SelectItem value="teacher">{t("teacher")}</SelectItem>
                    <SelectItem value="parent">{t("parent")}</SelectItem>
                    <SelectItem value="admin">{t("admin")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={save} data-testid="cu-save" className="w-full rounded-full h-11">{t("submit")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="rounded-2xl">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("fullName")}</TableHead>
                <TableHead>{t("email")}</TableHead>
                <TableHead>{t("role")}</TableHead>
                <TableHead>{t("phone")}</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="num">{u.email}</TableCell>
                  <TableCell><span className="rounded-full bg-muted px-2 py-1 text-xs">{u.role}</span></TableCell>
                  <TableCell className="num">{u.phone || "—"}</TableCell>
                  <TableCell><Button variant="ghost" size="sm" onClick={() => del(u.id)}>×</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export function StudentsAdminPage() {
  const { t } = useI18n();
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", level: "", school: "", parent_email: "", date_of_birth: "", notes: "" });

  const load = () => api.get("/students").then((r) => setStudents(r.data.students));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/students", form);
      toast.success("Student saved");
      setOpen(false);
      setForm({ full_name: "", level: "", school: "", parent_email: "", date_of_birth: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this student?")) return;
    try { await api.delete(`/students/${id}`); load(); toast.success("Deleted"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-students">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("students")}</h1>
          <p className="text-sm text-muted-foreground">All registered students.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-student-btn" className="rounded-full gap-2">
              <Plus className="h-4 w-4" />
              {t("registerChild")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("registerChild")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("fullName")}</Label><Input data-testid="cs-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="h-11" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("level")}</Label><Input data-testid="cs-level" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="h-11" placeholder="Primary 3" /></div>
                <div><Label>{t("dateOfBirth")}</Label><Input data-testid="cs-dob" type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="h-11" /></div>
              </div>
              <div><Label>{t("school")}</Label><Input data-testid="cs-school" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} className="h-11" /></div>
              <div><Label>Parent {t("email")}</Label><Input data-testid="cs-parent-email" type="email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} className="h-11" placeholder="parent@example.com" /></div>
              <Button onClick={save} data-testid="cs-save" className="w-full h-11 rounded-full">{t("saveChild")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="rounded-2xl">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("fullName")}</TableHead>
                <TableHead>{t("level")}</TableHead>
                <TableHead>{t("school")}</TableHead>
                <TableHead>Parent</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {students.map((s) => (
                <TableRow key={s.id} data-testid={`stu-row-${s.id}`}>
                  <TableCell className="font-medium">{s.full_name}</TableCell>
                  <TableCell className="num">{s.level}</TableCell>
                  <TableCell>{s.school || "—"}</TableCell>
                  <TableCell className="num text-sm">{s.parent_email || "—"}</TableCell>
                  <TableCell><Button variant="ghost" size="sm" onClick={() => del(s.id)}>×</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export function AttendanceAdminPage() {
  const { t } = useI18n();
  const [staff, setStaff] = useState([]);
  const [students, setStudents] = useState([]);
  useEffect(() => {
    api.get("/attendance/staff").then((r) => setStaff(r.data.records)).catch(() => {});
    api.get("/attendance/student").then((r) => setStudents(r.data.records)).catch(() => {});
  }, []);
  return (
    <div className="space-y-8" data-testid="admin-attendance">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("attendance")}</h1>
      </div>
      <section>
        <h2 className="mb-3 text-lg font-semibold">{t("staff")} & {t("teacher")}</h2>
        <Card className="rounded-2xl"><CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>{t("role")}</TableHead><TableHead>Method</TableHead><TableHead>Action</TableHead><TableHead>When</TableHead>
            </TableRow></TableHeader>
            <TableBody>{staff.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.user_name}</TableCell>
                <TableCell>{r.user_role}</TableCell>
                <TableCell className="num">{r.method}</TableCell>
                <TableCell><span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">{r.action}</span></TableCell>
                <TableCell className="num text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</TableCell>
              </TableRow>
            ))}{staff.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">{t("noRecordsYet")}</TableCell></TableRow>}</TableBody>
          </Table>
        </CardContent></Card>
      </section>
      <section>
        <h2 className="mb-3 text-lg font-semibold">{t("students")}</h2>
        <Card className="rounded-2xl"><CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Student</TableHead><TableHead>{t("level")}</TableHead><TableHead>Method</TableHead><TableHead>Status</TableHead><TableHead>When</TableHead>
            </TableRow></TableHeader>
            <TableBody>{students.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.student_name}</TableCell>
                <TableCell className="num">{r.level}</TableCell>
                <TableCell className="num">{r.method}</TableCell>
                <TableCell><span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">{r.status}</span></TableCell>
                <TableCell className="num text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</TableCell>
              </TableRow>
            ))}{students.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">{t("noRecordsYet")}</TableCell></TableRow>}</TableBody>
          </Table>
        </CardContent></Card>
      </section>
    </div>
  );
}

export function PaymentsAdminPage() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/payments/history").then((r) => setRows(r.data.transactions)).catch(() => {});
  }, []);
  return (
    <div className="space-y-6" data-testid="admin-payments">
      <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("payments")}</h1>
      <Card className="rounded-2xl"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>User</TableHead><TableHead>Package</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>When</TableHead>
          </TableRow></TableHeader>
          <TableBody>{rows.map((r) => (
            <TableRow key={r.session_id}>
              <TableCell className="num text-sm">{r.user_id}</TableCell>
              <TableCell>{r.package_name}</TableCell>
              <TableCell className="num font-semibold">{(r.currency || "").toUpperCase()} {Number(r.amount).toFixed(2)}</TableCell>
              <TableCell>
                <span className={`rounded-full px-2 py-0.5 text-xs ${r.payment_status === "paid" ? "bg-primary/10 text-primary" : "bg-muted"}`}>
                  {r.payment_status}
                </span>
              </TableCell>
              <TableCell className="num text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</TableCell>
            </TableRow>
          ))}{rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">{t("noRecordsYet")}</TableCell></TableRow>}</TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
